import { useSyncExternalStore } from 'react';

// Themes are blocks of CSS tokens in styles.css, chosen by data-theme on <html>.
// Each theme has a light and a dark scheme, chosen by data-scheme: the person's choice,
// or by default the device's setting, followed live when it changes. Both are per-browser
// preferences for now; a workspace default can set the same attributes later without touching components.

export const THEMES = [
  { id: 'mazu', label: 'Mazu (paper and coral)' },
  { id: 'evergreen', label: 'Evergreen (ink green and mint)' },
  { id: 'classic', label: 'Classic (paper and teal)' },
  { id: 'slate', label: 'Slate (grey and green)' },
  { id: 'frosted', label: 'Frosted (steel blue and frost)' },
] as const;
export type ThemeId = (typeof THEMES)[number]['id'];
export type Scheme = 'light' | 'dark';
export type SchemeChoice = 'device' | Scheme;
export const SCHEMES: Array<[SchemeChoice, string]> = [['device', 'Match this device'], ['light', 'Light'], ['dark', 'Dark']];

const KEY = 'll-theme';
const SCHEME_KEY = 'll-scheme';
const isTheme = (v: unknown): v is ThemeId => THEMES.some((t) => t.id === v);
// Ids a browser may have kept from before the themes were renamed (2026-11-02).
const RENAMED: Record<string, ThemeId> = { coral: 'mazu', ledgerloom: 'evergreen' };
const dark = () => window.matchMedia('(prefers-color-scheme: dark)');

export function currentTheme(): ThemeId {
  try {
    const v = localStorage.getItem(KEY);
    const id = v && RENAMED[v] ? RENAMED[v] : v;
    if (isTheme(id)) return id;
  } catch { /* storage blocked: use the default */ }
  return 'mazu';
}

export function currentSchemeChoice(): SchemeChoice {
  try {
    const v = localStorage.getItem(SCHEME_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch { /* storage blocked: match the device */ }
  return 'device';
}

export const currentScheme = (): Scheme => {
  const c = currentSchemeChoice();
  return c !== 'device' ? c : dark().matches ? 'dark' : 'light';
};

// Sets the theme, and keeps data-scheme with the device setting from then on (unless a scheme is chosen).
export function applyTheme(id: ThemeId = currentTheme()) {
  const root = document.documentElement;
  root.dataset.theme = id;
  if (!root.dataset.scheme) dark().addEventListener('change', () => { root.dataset.scheme = currentScheme(); });
  root.dataset.scheme = currentScheme();
}

export function setTheme(id: ThemeId) {
  try { localStorage.setItem(KEY, id); } catch { /* still applies for this visit */ }
  applyTheme(id);
}

const chosen = new EventTarget();
export function setScheme(c: SchemeChoice) {
  try { localStorage.setItem(SCHEME_KEY, c); } catch { /* still applies for this visit */ }
  document.documentElement.dataset.scheme = currentScheme();
  chosen.dispatchEvent(new Event('change'));
}

const subscribe = (cb: () => void) => {
  dark().addEventListener('change', cb);
  chosen.addEventListener('change', cb);
  return () => { dark().removeEventListener('change', cb); chosen.removeEventListener('change', cb); };
};
// The scheme for components that draw outside the token cascade (the toaster).
export const useScheme = () => useSyncExternalStore(subscribe, currentScheme);
